<template>
  <div class="ts-border ts-p-3 ts-rounded">
    <div class="row">
      <div class="col-md-6 ts-space-y-3">
        <div class="row">
          <label class="col-md-3 form-col-label control-label">{{
            $t('stockIn.purchaseNumber')
          }}</label>
          <div class="col-md-7">
            <a-select v-model="model.po_id" class="ts-w-full" :options="purchaseOrders" showSearch :filterOption="(input, option) =>
              option.componentOptions.children[0].text
                .toLowerCase()
                .indexOf(input.toLowerCase())
              " @change="purchaseOrderChange"></a-select>
          </div>
        </div>
        <div class="row">
          <label class="col-md-3 form-col-label control-label required">{{
            $t('stockIn.invoiceNumber')
          }}</label>
          <div class="col-md-7">
            <input v-model="model.invoice_no" type="text" class="form-control"
              :class="{ 'is-invalid': errors.has('invoice_no') }" />
            <div class="invalid-feedback" v-if="errors.has('invoice_no')">
              {{ errors.first('invoice_no') }}
            </div>
          </div>
        </div>
        <div class="row">
          <label class="col-md-3 form-col-label control-label required">{{
            $t('stockIn.vendor')
          }}</label>
          <div class="col-md-7">
            <a-select v-model="model.vendor_id" :options="suppliers" showSearch :filterOption="(input, option) =>
              option.componentOptions.children[0].text
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
              " @change="onVendorChange" class="ts-w-full"
              :class="{ 'is-invalid': errors.has('vendor_id') }"></a-select>
            <div class="invalid-feedback" v-if="errors.has('vendor_id')">
              {{ errors.first('vendor_id') }}
            </div>
          </div>
        </div>
        <div class="row">
          <label class="form-col-label control-label col-md-3">
            {{ $t('wholesale.creditAmount') }} /
            {{ $t('wholesale.creditDay') }}
          </label>
          <div class="col-md-7">
            <div class="ts-grid ts-grid-cols-2 ts-gap-3">
              <div>
                <Input :readonly="true" :value="creditAmount
                  ? $defaultCurrencyWithCode(
                    creditAmount,
                    default_precision
                  )
                  : 0
                  " type="text" class="ts-w-full" prefix="ios-cash" />
              </div>
              <div>
                <Input v-model.number="model.credit_day" type="number" class="ts-w-full"
                  prefix="ios-calendar-outline" />
                <div class="validate" v-if="errors.has('credit_day')">
                  {{ errors.first('credit_day') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <label class="col-md-3 form-col-label control-label required">{{
            $t('stockIn.location')
          }}</label>
          <div class="col-md-7">
            <div class="ts-border ts-rounded-md ts-p-2">
              <RadioGroup v-model="model.stock_in_warehouse_type" @on-change="onStockInType">
                <Radio label="vendor">
                  <span>{{ $t('stockIn.vendor') }}</span>
                </Radio>
                <Radio label="customer">
                  <span>{{ $t('stockIn.customer') }}</span>
                </Radio>
              </RadioGroup>
              <a-select :loading="loading" :disabled="model.stock_in_warehouse_type == 'vendor'" class="ts-w-full"
                v-model="model.location_id" :options="locations" showSearch :filterOption="(input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                  " :class="{ 'is-invalid': errors.has('location_id') }"></a-select>
            </div>
            <div class="invalid-feedback" v-if="errors.has('location_id')">
              {{ errors.first('location_id') }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6 ts-space-y-3">
        <div class="row">
          <label class="col-md-3 form-col-label control-label required">{{
            $t('stockIn.stockInDate')
          }}</label>
          <div class="col-md-7">
            <a-date-picker class="ts-w-full" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY" v-model="model.stock_in_date"
              :class="{ 'is-invalid': errors.has('stock_in_date') }"></a-date-picker>
            <div class="invalid-feedback" v-if="errors.has('stock_in_date')">
              {{ errors.first('stock_in_date') }}
            </div>
          </div>
        </div>
        <div class="row">
          <label class="col-md-3 form-col-label control-label">{{
            $t('stockIn.nextPaymentDate')
          }}</label>
          <div class="col-md-7">
            <a-date-picker class="ts-w-full" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY"
              v-model="model.next_payment_date" :class="{
                'is-invalid': errors.has('next_payment_date')
              }"></a-date-picker>
            <div class="invalid-feedback" v-if="errors.has('next_payment_date')">
              {{ errors.first('next_payment_date') }}
            </div>
          </div>
        </div>
        <div class="row">
          <label class="form-col-label control-label col-md-3">{{
            $t('stockIn.purchaseType')
          }}</label>
          <div class="col-md-7">
            <div class="ts-flex ts-items-center">
              <a-select v-model="model.purchase_type_id" :options="purchaseTypes" showSearch :showArrow="false"
                :filterOption="(input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                  " class="ts-w-full"></a-select>
              <a href="#" @click.prevent="() => (add_new_purchase_type = true)" v-tooltip="$t('add_new')"
                class="ts-bg-blue-500 ts-h-8 ts-w-8 ts-text-white ts-flex ts-items-center ts-rounded-tr ts-rounded-br hover:ts-text-white"><i
                  class="fas fa-plus-square ts-mx-auto"></i></a>
            </div>
            <div class="validate" v-if="errors.has('purchase_type_id')">
              {{ errors.first('purchase_type_id') }}
            </div>
          </div>
        </div>
        <div class="row">
          <label class="col-md-3 form-col-label control-label required">{{
            $t('stockIn.fixRate')
          }}</label>
          <div class="col-md-7">
            <input v-model="model.fix_rate" type="text" readonly class="form-control" />
          </div>
        </div>
        <div class="row">
          <label class="col-md-3 form-col-label control-label">{{
            $t('stockIn.remark')
          }}</label>
          <div class="col-md-7">
            <textarea v-model="model.remarks" rows="3" class="form-control ts-w-full"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Purchase Type -->
    <a-modal :title="$t('purchaseOrder.createPurchaseType')" width="30%" v-model="add_new_purchase_type" :footer="null">
      <div class="row">
        <label class="form-col-label control-label col-md-12 required">{{
          $t('purchaseOrder.purchaseType')
        }}</label>
        <div class="col-md-12">
          <input v-model="purchase_type_model.purchase_type" type="text" class="form-control" />
          <div class="validate" v-if="errors.has('purchase_type')">
            {{ errors.first('purchase_type') }}
          </div>
        </div>
      </div>
      <div class="row ts-mt-4">
        <div class="col-md-12 ts-flex ts-items-center ts-space-x-3 ts-justify-end">
          <ts-button @click.prevent="() => (add_new_purchase_type = false)">
            {{ $t('cancel') }}
          </ts-button>
          <ts-button :waiting="waiting_new_purchase_type" @click.prevent="onSavePurchaseType" color="primary">{{
            $t('save') }}</ts-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'info',
  props: ['value'],
  data() {
    return {
      loading: false,
      errors: new Errors(),
      add_new_purchase_type: false,
      waiting_new_purchase_type: false,
      model: {},
      purchase_type_model: {
        purchase_type: null
      }
    }
  },
  computed: {
    ...mapState('stock/stockInV2', ['formViewDatas']),
    purchaseOrders() {
      if (this.formViewDatas['purchaseOrders']) {
        return this.formViewDatas['purchaseOrders'].map(el => ({
          value: el.po_id,
          label: el.po_number
        }))
      }
      return []
    },
    suppliers() {
      if (this.formViewDatas['suppliers']) {
        return this.formViewDatas['suppliers'].map(el => ({
          value: el.vendor_id,
          label: el.vendor_name,
          credit_day: el.credit_day,
          credit_amount: el.credit_amount
        }))
      }
      return []
    },
    purchaseTypes() {
      if (this.formViewDatas['purchaseTypes']) {
        return this.formViewDatas['purchaseTypes'].map(el => ({
          value: el.purchase_type_id,
          label: el.purchase_type
        }))
      }
      return []
    },
    locations() {
      if (this.formViewDatas['locations']) {
        return this.formViewDatas['locations'].map(el => ({
          value: el.location_id,
          label: el.location_name_en
        }))
      }
      return []
    },
    default_precision() {
      return this.$store.state.authUser.sys_setting.decimal_digit
    },
    creditAmount() {
      let vendor = this.suppliers.find(v => v.value == this.model.vendor_id)
      if (vendor) {
        return vendor.credit_amount
      }
      return 0
    }
  },
  created() {
    this.model = this.value
  },
  methods: {
    ...mapActions('stock/stockInV2', ['getFormViewModel']),
    purchaseOrderChange(value) {
      this.loading = true
      return this.getFormViewModel({
        index: 'purchaseOrderDetail',
        params: {
          fnName: 'purchaseOrderDetail',
          po_id: value
        }
      })
        .then((response) => {
          this.setData(response.data.purchaseOrderDetail)
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    setData(data) {
      this.model.vendor_id = data.vendor_id
      this.model.credit_day = data.credit_day
      this.model.purchase_type_id = data.purchase_type_id
      this.model.product_details = []
      data.details.forEach(el => {

        const product = this.formViewDatas['products'].find(p => p.product_id == el.product_id)

        const uom = this.formViewDatas['unitOfMeasures'].find(uom => uom.uom_id == el.uom_id)

        this.model.product_details.push(
          {
            product_id: el.product_id,
            uom_id: el.uom_id,
            uom_cat_id: el.uom_cat_id,
            qty: el.quantity_order - el.quantity_received,
            input_cost: el.input_cost,
            cost: el.cost,
            input_discount_rate: el.input_discount_rate,
            discount_rate: el.discount_rate,
            discount_type: el.discount_type,
            input_total_after_discount: el.input_total_after_discount,
            total_after_discount: el.total_after_discount,
            vat_rate: 0,
            input_vat_amount: 0,
            vat_amount: 0,
            input_grand_total: el.input_total_after_discount,
            grand_total: el.total_after_discount,
            expiry_date: null,
            is_expire: product.is_expirable,
            require_imei: product.require_imei,
            product_grade: '',
            input_currency_id: el.input_currency_id,
            currency_id: el.currency_id,
            fx_rate: el.fx_rate,
            fx_rate_ratio: el.fx_rate_ratio,
            imei_list: [],
            stock_quantity: uom.stockout_quantity * (el.quantity_order - el.quantity_received)
          }
        )
      })
    },
    onSavePurchaseType() {
      this.waiting_new_purchase_type = true
      this.errors = new Errors()
      this.$store
        .dispatch('purchaseOrderV2/purchaseType/store', this.purchase_type_model)
        .then(async response => {
          this.$toasted.success(response.message)
          this.formViewDatas['purchaseTypes'].push(response.data)
          this.model.purchase_type_id = response.data.purchase_type_id
          this.purchase_type_model.purchase_type = ''
          this.add_new_purchase_type = false
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new_purchase_type = false
        })
    },
    onVendorChange(value, opt) {
      this.model.credit_day = opt.data.props.credit_day
    },
    onStockInType() {
      this.model.location_id = null
      this.location = []
      if (this.model.stock_in_warehouse_type == 'vendor') {
        this.model.location_id = this.$store.state.authUser.user.warehouses
          .filter(w => w.pivot.is_default == true)
          .reduce((i, v) => (i = v.location_id), null)
      } else {
        this.model.location_id = null
      }
    },
    updateNextPaymentDate() {
      if (this.model.stock_in_date && this.model.credit_day > 0) {
        this.model.next_payment_date = moment(
          this.model.stock_in_date,
          'DD-MM-YYYY'
        )
          .add(this.model.credit_day, 'days')
          .format('DD-MM-YYYY')
      } else {
        this.model.next_payment_date = null
      }
    },
  },
  watch: {
    validate: function (error) {
      this.errors = new Errors(error.errors)
    },
    'model.stock_in_date': function () {
      this.updateNextPaymentDate()
    },
    'model.credit_day': function () {
      this.updateNextPaymentDate()
    },
  }
}
</script>
